/**
 * PROGRESS and ATTENDANCE as seen by the STUDENT (D1 · E3) — PURE selectors.
 *
 * Everything they need comes from what `my_cohorts_student` already replicates
 * to their device: `attendance_record` filtered by `user_id`, `class_session`
 * of their cohorts, and `session_module` (the class plan). Nothing here
 * recomputes what the server already computes:
 *
 *  · The attendance **percentage** is derived by the server
 *    (`use_cases::attendance_stats::attendance_rate`) and denormalized into
 *    `cohort_enrollment`; it's read with `attendanceStatsOf` and is NOT
 *    recounted here.
 *  · The attendance **target** comes from the certification criterion
 *    (`academy.completion.attendance_pct`) evaluated by the server and served
 *    via `/api/cohorts/:id/my-completion`; here it's only extracted from the breakdown.
 *  · Class plan progress reuses `planProgress` (session-plan).
 *
 * What DOES belong to this layer: joining MY attendance records with the
 * session they belong to (the history), comparing the percentage against
 * the target, and composing the course's class grid.
 */

import type { AttendanceStats } from "../people/selectors";
import { type PlanCompletionCtx, type PlanModule, planProgress } from "../session-plan/types";

/** The four states of an `attendance_record`. */
export type AttendanceStatusValue = "present" | "late" | "absent" | "excused";

const ATTENDANCE_STATUSES: readonly AttendanceStatusValue[] = [
  "present",
  "late",
  "absent",
  "excused",
];

/** Coerces a row's raw status into a known one (`null` if it isn't). */
export function parseAttendanceStatus(
  status: string | null | undefined,
): AttendanceStatusValue | null {
  return ATTENDANCE_STATUSES.find((s) => s === status) ?? null;
}

/** Minimal `attendance_record` row (what arrives via sync). */
export interface AttendanceRecordRow {
  id: string;
  class_session_id?: string | null;
  cohort_id?: string | null;
  status?: string | null;
}

/** Minimal `class_session` row (what arrives via sync, no server-only). */
export interface ClassSessionRow {
  id: string;
  cohort_id?: string | null;
  date?: string | null;
  start_time?: string | null;
  topic?: string | null;
  deleted_at?: string | null;
}

/** A class in the student's attendance history. */
export interface AttendanceTimelineRow {
  sessionId: string;
  date: string | null;
  startTime: string | null;
  topic: string | null;
  status: AttendanceStatusValue;
}

/**
 * MY attendance history of a cohort, from the most recent class to the
 * oldest. Only LIVE sessions with an attendance record show up: no record
 * means no roll call happened, and painting an empty row would induce
 * reading it as an absence.
 */
export function attendanceTimeline(
  records: readonly AttendanceRecordRow[],
  sessions: readonly ClassSessionRow[],
): AttendanceTimelineRow[] {
  const sessionById = new Map<string, ClassSessionRow>();
  for (const s of sessions) {
    if (s.deleted_at == null) {
      sessionById.set(s.id, s);
    }
  }
  const rows: AttendanceTimelineRow[] = [];
  for (const r of records) {
    const session = r.class_session_id ? sessionById.get(r.class_session_id) : undefined;
    const status = parseAttendanceStatus(r.status);
    if (!session || !status) {
      continue;
    }
    rows.push({
      sessionId: session.id,
      date: session.date ?? null,
      startTime: session.start_time ?? null,
      topic: session.topic ?? null,
      status,
    });
  }
  return rows.toSorted(
    (a, b) =>
      (b.date ?? "").localeCompare(a.date ?? "") ||
      (b.startTime ?? "").localeCompare(a.startTime ?? "") ||
      b.sessionId.localeCompare(a.sessionId),
  );
}

/** Breakdown of a certification criterion (shape of `CriterionBreakdown`). */
export interface CriterionProgressRow {
  key: string;
  met: boolean;
  current: number;
  target: number;
}

/** My attendance against the cohort's certification criterion. */
export interface AttendanceCertification {
  /** Percentage 0..100 reached, or `null` if there's no record yet. */
  ratePct: number | null;
  /** Minimum required 0..100, or `null` if the cohort doesn't require attendance. */
  targetPct: number | null;
  /** `true`/`false` against the target; `null` with no target or no data. */
  met: boolean | null;
  /** Is there anything to show (percentage or target)? */
  hasData: boolean;
}

/**
 * Compares MY attendance percentage against the certification criterion's minimum.
 *
 * The preferred percentage is the one from the criterion evaluated by the
 * SERVER (same count, already rounded); the denormalized enrollment stats
 * are the fallback when the cohort has no configured rules and therefore
 * there's no criterion to consult.
 */
export function attendanceCertification(
  stats: AttendanceStats,
  criteria: readonly CriterionProgressRow[] = [],
): AttendanceCertification {
  const criterion = criteria.find((c) => c.key === "attendance_pct");
  const ratePct = criterion ? criterion.current : stats.rate;
  const targetPct = criterion ? criterion.target : null;
  return {
    ratePct,
    targetPct,
    met: criterion ? criterion.met : null,
    hasData: ratePct !== null || targetPct !== null,
  };
}

/** A class in the student's progress grid. */
export interface CohortSessionProgressRow {
  sessionId: string;
  date: string | null;
  startTime: string | null;
  topic: string | null;
  /** Recorded attendance state, or `null` if no roll call happened. */
  attendance: AttendanceStatusValue | null;
  /** Plan items done / that score (0/0 if the class has no plan). */
  planDone: number;
  planTotal: number;
}

/** Aggregated summary of the grid (the course's overall bar). */
export interface CohortProgressSummary {
  sessions: CohortSessionProgressRow[];
  /** Classes with `present` or `late` attendance (the server's criterion). */
  attended: number;
  /** Classes with a recorded roll call. */
  recorded: number;
  planDone: number;
  planTotal: number;
}

/**
 * "My N classes" grid of a cohort: for each live class, whether I attended
 * and how much of the plan I've done. Per-class progress is counted by
 * `planProgress` (session-plan) — there's no second definition of "done" here.
 */
export function cohortSessionsProgress(
  sessions: readonly ClassSessionRow[],
  planBySession: ReadonlyMap<string, readonly PlanModule[]>,
  planCtx: PlanCompletionCtx,
  records: readonly AttendanceRecordRow[],
): CohortProgressSummary {
  const statusBySession = new Map<string, AttendanceStatusValue>();
  for (const r of records) {
    const status = parseAttendanceStatus(r.status);
    if (r.class_session_id && status) {
      statusBySession.set(r.class_session_id, status);
    }
  }

  const rows = sessions
    .filter((s) => s.deleted_at == null)
    .toSorted(
      (a, b) =>
        (a.date ?? "").localeCompare(b.date ?? "") ||
        (a.start_time ?? "").localeCompare(b.start_time ?? "") ||
        a.id.localeCompare(b.id),
    )
    .map((s): CohortSessionProgressRow => {
      const plan = planProgress(planBySessionOrEmpty(planBySession, s.id), planCtx);
      return {
        sessionId: s.id,
        date: s.date ?? null,
        startTime: s.start_time ?? null,
        topic: s.topic ?? null,
        attendance: statusBySession.get(s.id) ?? null,
        planDone: plan.done,
        planTotal: plan.total,
      };
    });

  return {
    sessions: rows,
    attended: rows.filter((r) => r.attendance === "present" || r.attendance === "late").length,
    recorded: rows.filter((r) => r.attendance !== null).length,
    planDone: rows.reduce((acc, r) => acc + r.planDone, 0),
    planTotal: rows.reduce((acc, r) => acc + r.planTotal, 0),
  };
}

const EMPTY_PLAN: readonly PlanModule[] = [];

function planBySessionOrEmpty(
  planBySession: ReadonlyMap<string, readonly PlanModule[]>,
  sessionId: string,
): readonly PlanModule[] {
  return planBySession.get(sessionId) ?? EMPTY_PLAN;
}
